import GetPirschData from '@/graphQlQueries/queries/getPirschData';
import GetProjectUrlHistory from '@/graphQlQueries/queries/getProjectUrlHistory';
import GetUrlHistoryClashes from '@/graphQlQueries/queries/getUrlHistoryClashes';
import {
  normalizeSlugPath, buildPathPattern, escapeRegex, rewriteToCurrent, urlLineage,
} from '@/utils/analyticsPath';

// Pirsch only holds data from this date onward (analytics-provider switch).
export const SWITCH_DATE = new Date('2024-04-01T00:00:00');
// Earliest selectable date in the range picker.
export const MIN_DATE = new Date('2022-06-21T00:00:00');

export function formatTimeString(value) {
  if (!value) return '0';
  const totalSeconds = Math.floor(value);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60);
  return (minutes > 0)
    ? `${minutes}min ${seconds}s`
    : `${seconds}s`;
}

export function getGlobalStats(pirschData, sessionDuration) {
  const visitors = parseInt(pirschData?.visitors || 0, 10) || '-';
  const views = parseInt(pirschData?.views || 0, 10) || '-';
  const avgTime = parseFloat(sessionDuration) || 0;
  const bounceRate = parseFloat(pirschData?.bounce_rate || 0);

  return {
    visitors,
    views,
    avgTime,
    bounceRate: `${Math.round(bounceRate * 100)}%`,
  };
}

export function averageSessionDuration(result) {
  if (!result) return 0;
  let addedTime = 0;
  let counter = 0;
  result.forEach((item) => {
    if (item.average_time_spent_seconds) {
      addedTime += item.average_time_spent_seconds;
      counter += 1;
    }
  });
  return (counter > 0) ? Math.round(addedTime) / counter : 0;
}

export function getReadsPerDay(pirschData) {
  const readsPerDayData = [];
  if (pirschData) {
    pirschData.forEach((item) => {
      readsPerDayData.push({
        label: item.day.split('T')[0],
        value: item.views,
      });
    });
  }
  readsPerDayData.sort((a, b) => a.label.localeCompare(b.label));
  return readsPerDayData;
}

export function buildPirschParams({
  pirschCode, startDate, endDate, pattern,
}) {
  const switchIso = SWITCH_DATE.toISOString();
  const from = startDate > switchIso
    ? startDate.split('T')[0]
    : switchIso.split('T')[0];
  return {
    id: pirschCode,
    from,
    to: endDate.split('T')[0],
    pattern,
  };
}

export function isRangeInPirsch(endDateIso) {
  return new Date(endDateIso) >= SWITCH_DATE;
}

async function queryPirsch(apollo, endpoint, params) {
  const result = await apollo.query({
    query: GetPirschData,
    variables: { obj: { endpoint, params } },
  });
  return result.data.getPirschData.result;
}

// Fields that are shares or averages: merged weighted by visitors, never added.
const WEIGHTED_FIELD = /rate|average|avg|relative|^cr$/;
// Fields that may differ between two requests for the same row.
const NOT_A_KEY = new Set(['title', 'referrer_icon']);

function rowKey(row) {
  return JSON.stringify(Object.keys(row)
    .filter((k) => typeof row[k] !== 'number' && !NOT_A_KEY.has(k))
    .sort()
    .map((k) => [k, row[k]]));
}

function combineRows(rows) {
  if (rows.length === 1) return rows[0];
  const weight = (row) => row.visitors ?? row.views ?? 1;
  const totalWeight = rows.reduce((sum, row) => sum + weight(row), 0);
  const out = { ...rows[0] };
  Object.keys(out).forEach((k) => {
    if (typeof out[k] !== 'number') return;
    out[k] = WEIGHTED_FIELD.test(k)
      ? rows.reduce((sum, row) => sum + (row[k] || 0) * (totalWeight ? weight(row) : 1), 0)
        / (totalWeight || rows.length)
      : rows.reduce((sum, row) => sum + (row[k] || 0), 0);
  });
  return out;
}

/**
 * Put the answers of several Pirsch requests (one per URL segment) back
 * together. Rows that describe the same thing (same path, day, referrer, ...)
 * become one: counts are added, shares and averages weighted by visitors.
 * ponytail: unique visitors are added too, so someone who read the page under
 * the old and the new URL counts twice; Pirsch cannot dedupe across requests.
 */
export function mergePirschResults(results) {
  const parts = (results || []).filter((r) => r !== null && r !== undefined);
  if (!parts.length) return null;
  if (!Array.isArray(parts[0])) return combineRows(parts);
  const groups = new Map();
  parts.flat().forEach((row) => {
    const key = rowKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.values()].map(combineRows);
}

function withCurrentPaths(result, lineage) {
  if (!Array.isArray(result)) return result;
  return result.map((row) => ((row && typeof row.path === 'string')
    ? { ...row, path: rewriteToCurrent(normalizeSlugPath(row.path), lineage) }
    : row));
}

// The segment's window laid over the requested range; null if they miss.
function segmentParams(params, segment) {
  const from = (segment.from && segment.from > params.from) ? segment.from : params.from;
  const to = (segment.to && segment.to < params.to) ? segment.to : params.to;
  return from > to ? null : { ...params, from, to };
}

/**
 * One Pirsch statistic for the dashboard's item.
 *
 * With `opts.lineage` (see urlLineage) it covers every URL the project held:
 * one request per segment, old paths mapped onto the current URL, rows merged.
 * Without it, it is a single request with `opts.pattern`.
 */
export async function fetchStat(apollo, endpoint, opts, extra) {
  const params = { ...buildPirschParams(opts), ...extra };
  const { lineage } = opts;
  if (!lineage) return queryPirsch(apollo, endpoint, params);
  const requests = lineage.segments
    .map((segment) => {
      const windowed = segmentParams(params, segment);
      return windowed && { ...windowed, pattern: buildPathPattern(segment.paths) };
    })
    .filter(Boolean);
  const results = await Promise.all(requests.map((p) => queryPirsch(apollo, endpoint, p)));
  return mergePirschResults(results.map((r) => withCurrentPaths(r, lineage)));
}

/**
 * The URL lineage (see urlLineage) of each project, keyed by project id.
 *
 * Two requests for any number of projects: their own history, then every row
 * in their groups on one of those paths or below, to find clashes. Rows from
 * an earlier group are dropped: a cross-group move starts fresh analytics
 * (different domain, spec R3). Projects without a public path are left out.
 * ponytail: the clash rows pass the user's project permissions, so a project
 * the user cannot see does not bracket the dates; sysadmins see all.
 */
export async function loadUrlLineages(apollo, projectIds) {
  const ids = [...new Set(projectIds || [])].filter((id) => id != null);
  const lineages = new Map();
  if (!ids.length) return lineages;

  const { data } = await apollo.query({
    query: GetProjectUrlHistory,
    variables: { projectIds: ids },
    fetchPolicy: 'network-only',
  });
  const lookup = new Map((data.item_lookup || []).map((row) => [row.project_id, row]));
  const own = new Map();
  (data.project_url_history || []).forEach((row) => {
    const entry = lookup.get(row.project_id);
    if (!entry || row.group_id !== entry.group_id) return;
    if (!own.has(row.project_id)) own.set(row.project_id, []);
    own.get(row.project_id).push(row);
  });

  const paths = [...new Set([...own.values()].flat().map((row) => normalizeSlugPath(row.slug_path)))];
  let others = [];
  if (paths.length) {
    const result = await apollo.query({
      query: GetUrlHistoryClashes,
      variables: {
        where: {
          group_id: { _in: [...new Set([...lookup.values()].map((row) => row.group_id))] },
          _or: paths.flatMap((p) => [{ slug_path: { _eq: p } }, { slug_path: { _like: `${p}/%` } }]),
        },
      },
      fetchPolicy: 'network-only',
    });
    others = result.data.project_url_history || [];
  }

  ids.forEach((id) => {
    const entry = lookup.get(id);
    if (!entry || !entry.slug_path) return;
    const groupOthers = others.filter((row) => row.group_id === entry.group_id);
    lineages.set(id, urlLineage(entry.slug_path, own.get(id) || [], groupOthers));
  });
  return lineages;
}

export async function fetchGlobalStats(apollo, opts) {
  return fetchStat(apollo, '/statistics/total', opts);
}

export async function fetchSessionDuration(apollo, opts) {
  const result = await fetchStat(apollo, '/statistics/duration/session', opts, { scale: 'year' });
  return averageSessionDuration(result);
}

export async function fetchReadsPerDay(apollo, opts) {
  return fetchStat(apollo, '/statistics/visitor', opts);
}

/**
 * Event views for the dashboard.
 *
 * All of these read the raw rows of /statistics/event/list, where every row is
 * one distinct combination of event name and metadata. The event names are the
 * ones the tracking layer sends; see docs/plans/analytics-integration.md 5.
 */

export const EVENT_LABELS = {
  'Navigate internal': 'Sprung im Magazin',
  'Navigate external': 'Klick auf fremde Seite',
  Download: 'Download',
  Contact: 'Mail oder Telefon',
  'Scroll depth': 'Scroll-Tiefe',
  Form: 'Formular',
  Custom: 'Eigenes Ereignis',
};

export function eventLabel(name) {
  if (!name) return '';
  return EVENT_LABELS[name] || name;
}

const FORM_STEPS = ['viewed', 'started', 'abandoned', 'submitted'];

export function formFunnel(events) {
  const byForm = new Map();
  (events || []).forEach((event) => {
    if (event.name !== 'Form') return;
    const { meta } = event;
    if (!meta) return;
    const form = meta.label;
    const step = meta.action;
    if (!form || FORM_STEPS.indexOf(step) === -1) return;
    if (!byForm.has(form)) {
      byForm.set(form, {
        form, name: form, viewed: 0, started: 0, abandoned: 0, submitted: 0,
      });
    }
    const entry = byForm.get(form);
    entry[step] += event.visitors;
    // Only the server knows the human name, and only on the submitted step.
    if (meta.form_name) entry.name = meta.form_name;
  });

  return Array.from(byForm.values())
    .map((entry) => ({
      ...entry,
      completionRate: entry.viewed ? Math.round((entry.submitted / entry.viewed) * 100) : 0,
    }))
    .sort((a, b) => b.viewed - a.viewed);
}

const SCROLL_STEPS = [25, 50, 75, 100];

/**
 * Visitors per scroll step, panels left out.
 *
 * A scroll inside a small box is not a reading depth. The runtime marks those
 * as `scroll_panel`; rows without an action come from before that change and
 * were all page scrolls, so they still count.
 */
function stepVisitors(events) {
  const visitors = {};
  (events || []).forEach((event) => {
    if (event.name !== 'Scroll depth') return;
    const meta = event.meta || {};
    if (meta.action === 'scroll_panel') return;
    const percent = parseInt(meta.label || '', 10);
    if (SCROLL_STEPS.indexOf(percent) === -1) return;
    visitors[percent] = (visitors[percent] || 0) + event.visitors;
  });
  return visitors;
}

/**
 * The reading funnel.
 *
 * With `totalVisitors` every step is measured against everyone who was there,
 * which is what a reader expects from a percentage. Without it the first step
 * is the base, the way it worked before the number had a caption.
 */
export function scrollDepth(events, totalVisitors) {
  const visitors = stepVisitors(events);
  const base = totalVisitors || visitors[25] || 0;
  return SCROLL_STEPS.map((percent) => ({
    percent,
    visitors: visitors[percent] || 0,
    share: base ? Math.round(((visitors[percent] || 0) / base) * 100) : 0,
  }));
}

/**
 * How far the average visitor read, in percent.
 *
 * Every visitor counts once, with the deepest step they reached. Steps are
 * cascaded backwards from the deepest one, because data from before the
 * funnel was made monotone can have a later step larger than an earlier one.
 */
export function averageReadDepth(events) {
  const visitors = stepVisitors(events);
  const at = (percent) => visitors[percent] || 0;
  // Cascade from the deepest step backwards: reaching a later step implies
  // reaching every step before it, even when the raw counts disagree.
  const reached = [];
  for (let i = SCROLL_STEPS.length - 1; i >= 0; i -= 1) {
    const deeper = (i + 1 < SCROLL_STEPS.length) ? reached[i + 1] : 0;
    reached[i] = Math.max(at(SCROLL_STEPS[i]), deeper);
  }
  const base = reached[0];
  if (!base) return 0;
  let total = 0;
  for (let i = 0; i < SCROLL_STEPS.length; i += 1) {
    const next = (i + 1 < SCROLL_STEPS.length) ? reached[i + 1] : 0;
    total += SCROLL_STEPS[i] * (reached[i] - next);
  }
  return Math.round(total / base);
}

/** Visitors who scrolled inside a box rather than the page itself. */
export function panelScrollTotal(events) {
  let total = 0;
  (events || []).forEach((event) => {
    if (event.name !== 'Scroll depth') return;
    if (!event.meta || event.meta.action !== 'scroll_panel') return;
    if (parseInt(event.meta.label || '', 10) !== 25) return;
    total += event.visitors;
  });
  return total;
}

export function linkList(events, eventName, limit) {
  const rows = (events || [])
    .filter((event) => event.name === eventName && event.meta && event.meta.label)
    .map((event) => ({
      label: event.meta.label,
      visitors: event.visitors,
      count: event.count,
    }))
    .sort((a, b) => b.visitors - a.visitors);
  return limit ? rows.slice(0, limit) : rows;
}

/**
 * Make a tracked link readable in a narrow table cell.
 *
 * Drops the scheme and a leading www so the domain comes first, then cuts out
 * the middle rather than the end: the host and the last path segment are the
 * two parts that tell you what was clicked.
 */
export function shortenUrl(value, max) {
  const raw = (value || '').trim();
  if (!raw) return '';
  const limit = max || 60;
  const stripped = raw
    .replace(/^https?:\/\//i, '')
    .replace(/^mailto:/i, '')
    .replace(/^tel:/i, '')
    .replace(/^www\./i, '');
  if (stripped.length <= limit) return stripped;
  // Reserve one character for the ellipsis, then favour the tail slightly less
  // than the head so the domain always survives.
  const head = Math.ceil((limit - 1) * 0.6);
  const tail = limit - 1 - head;
  return `${stripped.slice(0, head)}…${stripped.slice(stripped.length - tail)}`;
}

/**
 * Visitors per page for one event, ready to look up by page path.
 *
 * Pirsch reports the raw path; the dashboard matches on the normalized one,
 * exactly as `setChildPageData` does for the page table.
 */
export function visitorsByPath(rows) {
  const byPath = {};
  (rows || []).forEach((row) => {
    if (!row || !row.path) return;
    byPath[normalizeSlugPath(row.path)] = row.visitors || 0;
  });
  return byPath;
}

/**
 * Who reached a given scroll step on each page.
 *
 * One request per step for the whole table: the filter pins the event, the
 * origin and the step, so every row is "visitors of this page who got here".
 */
export async function fetchScrollStepByPage(apollo, opts, step) {
  return fetchStat(apollo, '/statistics/event/page', opts, {
    event: 'Scroll depth',
    meta_action: 'scroll',
    meta_label: String(step),
  });
}

/**
 * Of the readers who scrolled a page at all, how many got halfway.
 *
 * Both numbers come from the same event stream, so the share stays honest even
 * while scroll tracking is younger than the selected period — measuring
 * against every visitor would divide fresh events by an old visitor count and
 * make every page look unread. Without a scroller the caller gets null and
 * shows a dash rather than a confident zero.
 */
export function readSharePercent(halfway, scrolled) {
  if (!scrolled) return null;
  return Math.min(Math.round(((halfway || 0) / scrolled) * 100), 100);
}

/**
 * Everything that happened on one page.
 *
 * The exact path replaces the pattern: keeping both would only narrow the
 * result, and the path is already inside the pattern's subtree.
 */
export async function fetchPageEvents(apollo, opts) {
  const { pattern, ...params } = buildPirschParams(opts);
  const { lineage, path } = opts;
  if (!lineage) return queryPirsch(apollo, '/statistics/event/list', { ...params, path });
  // The same page under each URL of a segment, as one exact-match pattern.
  if (!path.startsWith(lineage.current)) {
    return queryPirsch(apollo, '/statistics/event/list', { ...params, path });
  }
  const tail = path.slice(lineage.current.length);
  const results = await Promise.all(lineage.segments.map((segment) => {
    const windowed = segmentParams(params, segment);
    if (!windowed) return null;
    const alternatives = segment.paths.map((p) => escapeRegex(`${p}${tail}`)).join('|');
    return queryPirsch(apollo, '/statistics/event/list', { ...windowed, pattern: `^(${alternatives})$` });
  }));
  return mergePirschResults(results);
}

/**
 * Path rows with the page name in front.
 *
 * Entry and exit pages come back as bare paths. A reader wants the name of the
 * page; an unpublished or deleted page has none, so the path stays.
 */
// "/a/b/leitfaden-zur-einfuehrung" -> "Leitfaden zur einfuehrung"
function titleFromPath(path) {
  const slug = path.split('/').filter(Boolean).pop() || path;
  const words = slug.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function namePathRows(rows, nameByPath, countKey, limit) {
  const rateKey = countKey === 'entries' ? 'entry_rate' : 'exit_rate';
  return (rows || [])
    .filter((row) => row && row.path)
    .map((row) => {
      const path = normalizeSlugPath(row.path);
      return {
        name: nameByPath[path] || titleFromPath(path),
        path,
        visitors: row.visitors || 0,
        count: row[countKey] || 0,
        rate: Math.round((row[rateKey] || 0) * 100),
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, limit || 10);
}

export async function fetchEntryPages(apollo, opts) {
  return fetchStat(apollo, '/statistics/page/entry', opts);
}

export async function fetchExitPages(apollo, opts) {
  return fetchStat(apollo, '/statistics/page/exit', opts);
}

/**
 * One arrow next to a head figure.
 *
 * Pirsch reports growth as a decimal against the equally long period before.
 * `lowerIsBetter` exists for the bounce rate, where a fall is the good news.
 */
export function growthBadge(value, lowerIsBetter) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const percent = Math.round(value * 100);
  if (percent === 0) return { percent: 0, direction: 'flat', tone: 'neutral' };
  const rising = percent > 0;
  return {
    percent,
    direction: rising ? 'up' : 'down',
    tone: (rising !== !!lowerIsBetter) ? 'good' : 'bad',
  };
}

// Growth compares against the period before, which only works as one request
// over the whole range; split or windowed URLs leave the arrows out.
export async function fetchGrowth(apollo, opts) {
  const { lineage } = opts;
  if (lineage && (lineage.segments.length !== 1
    || lineage.segments[0].from || lineage.segments[0].to)) return null;
  return queryPirsch(apollo, '/statistics/growth', {
    ...buildPirschParams(opts),
    ...(lineage && { pattern: buildPathPattern(lineage.segments[0].paths) }),
  });
}

export async function fetchChannels(apollo, opts) {
  return fetchStat(apollo, '/statistics/channel', opts);
}

export async function fetchCampaigns(apollo, opts) {
  return fetchStat(apollo, '/statistics/utm/campaign', opts);
}
